import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "./AppStateProvider";
import { useAuth } from "./AuthProvider";
import PhoneVerificationModal from "./components/ComponentsUI/PhoneVerificationModal/PhoneVerificationModal";
import PreferencesService from "./services/preferences.service";
import UserService from "./services/user.service";

interface PhoneVerificationContextType {
    hasVerifiedPhone: boolean;
    requirePhone: (onSuccess: () => void) => void;
    refreshPhoneStatus: (verified?: boolean) => void;
}

const PhoneVerificationContext = createContext<PhoneVerificationContextType>({
    hasVerifiedPhone: true,
    requirePhone: () => {},
    refreshPhoneStatus: () => {},
});

export const usePhoneVerification = () => useContext(PhoneVerificationContext);

interface PhoneVerificationProviderProps {
    children: React.ReactNode;
}

export const PhoneVerificationProvider: React.FC<PhoneVerificationProviderProps> = ({ children }) => {
    // Default to true so we never flash the gate while auth/data is still settling.
    // determinePhoneStatus() is the single source of truth and flips this reactively.
    const [hasVerifiedPhone, setHasVerifiedPhone] = useState<boolean>(true);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const pendingCallbackRef = useRef<(() => void) | null>(null);

    const { user, isAuthenticated, isLoading } = useAuth();
    const { state } = useContext(AppContext);
    const authUid = user?.uid;
    const stateUserId = state.user?.id;

    // Reactively decide whether the *current* signed-in user still needs to verify a phone.
    // This runs whenever auth settles — on login, on sign-up, and on reload alike — so the gate
    // engages the moment the user is authenticated, not only after a refresh.
    const determinePhoneStatus = useCallback(async () => {
        // Wait for auth to settle before deciding anything.
        if (isLoading) return;

        // Logged out: nothing to gate. (requirePhone is only reached behind auth anyway.)
        if (!isAuthenticated || !authUid) {
            setHasVerifiedPhone(true);
            return;
        }

        // Resolve the backend user id — prefer already-loaded app state, fall back to a lookup
        // (both are error-safe and return falsy rather than throwing).
        let infdId = stateUserId || undefined;
        if (!infdId) {
            const record = await UserService.getUserByAuthId(authUid);
            infdId = record?.id || undefined;
        }

        // Source of truth: a user with a verified phone on the backend is always allowed through.
        if (infdId) {
            const hasPhone = await UserService.getUserPhoneStatus(infdId);
            if (hasPhone) {
                await PreferencesService.clearPhoneVerificationRequired(authUid);
                await PreferencesService.clearPhoneVerificationPending();
                setHasVerifiedPhone(true);
                return;
            }
        }

        // No verified phone yet. New sign-up (must verify) or legacy user (grandfathered)?
        // A sign-up sets a device-global "pending" flag before auth (no uid yet). The first time
        // we see an authenticated user with that flag, bind the requirement to THIS uid and clear
        // the flag — so it can never leak to a different account on the same device.
        if (await PreferencesService.isPhoneVerificationPending()) {
            await PreferencesService.setPhoneVerificationRequired(authUid);
            await PreferencesService.clearPhoneVerificationPending();
        }

        const required = await PreferencesService.isPhoneVerificationRequired(authUid);
        setHasVerifiedPhone(!required);
    }, [isAuthenticated, isLoading, authUid, stateUserId]);

    useEffect(() => {
        determinePhoneStatus();
    }, [determinePhoneStatus]);

    const refreshPhoneStatus = useCallback((verified?: boolean) => {
        if (verified !== undefined) {
            setHasVerifiedPhone(verified);
        } else {
            determinePhoneStatus();
        }
    }, [determinePhoneStatus]);

    const requirePhone = useCallback((onSuccess: () => void) => {
        if (hasVerifiedPhone) {
            onSuccess();
            return;
        }
        pendingCallbackRef.current = onSuccess;
        setIsModalOpen(true);
    }, [hasVerifiedPhone]);

    const handleVerified = useCallback(() => {
        setHasVerifiedPhone(true);
        setIsModalOpen(false);
        if (pendingCallbackRef.current) {
            pendingCallbackRef.current();
            pendingCallbackRef.current = null;
        }
    }, []);

    const handleDismiss = useCallback(() => {
        setIsModalOpen(false);
        pendingCallbackRef.current = null;
    }, []);

    return (
        <PhoneVerificationContext.Provider value={{ hasVerifiedPhone, requirePhone, refreshPhoneStatus }}>
            {children}
            <PhoneVerificationModal
                isOpen={isModalOpen}
                onDismiss={handleDismiss}
                onVerified={handleVerified}
            />
        </PhoneVerificationContext.Provider>
    );
};
